import { Component, inject, output, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';

import { NotificationQuery } from '@core/query';
import { Notification } from '@core/types';

@Component({
  selector: 'app-notification-panel',
  standalone: true,
  templateUrl: './notification-panel.component.html',
  styleUrl: './notification-panel.component.css',
  imports: [CommonModule, RouterModule, TranslateModule, ButtonModule],
})
export class NotificationPanelComponent {
  private readonly notificationQuery = inject(NotificationQuery);
  private readonly router = inject(Router);
  private readonly messageService = inject(MessageService);
  private readonly translateService = inject(TranslateService);

  closed = output<void>();

  summary = this.notificationQuery.useSummary();
  markAsRead = this.notificationQuery.useMarkAsRead();
  markAllAsRead = this.notificationQuery.useMarkAllAsRead();

  isLoading = computed(() => this.summary.isLoading());
  isError = computed(() => this.summary.isError());
  error = computed(() => this.summary.error());
  unreadCount = computed(() => this.summary.data()?.unreadCount ?? 0);
  notifications = computed(() => {
    const list = this.summary.data()?.notifications ?? [];
    // El backend ya ordena descendente; aquí nos aseguramos de mantener el orden
    // y filtrar posibles duplicados por id sin alterar el orden.
    const seen = new Set<string>();
    return list.filter((item) => {
      if (seen.has(item.id)) {
        return false;
      }
      seen.add(item.id);
      return true;
    });
  });

  onMarkAllAsRead(event: Event): void {
    event.stopPropagation();
    this.markAllAsRead.mutate(undefined, {
      onError: () => {
        this.showErrorToast('NOTIFICATIONS.MARK_ALL_READ_ERROR');
      },
    });
  }

  onNotificationClick(notification: Notification): void {
    if (notification.status === 'unread') {
      this.markAsRead.mutate(notification.id, {
        onSuccess: () => {
          this.navigateIfNeeded(notification);
        },
        onError: () => {
          this.navigateIfNeeded(notification);
        },
      });
    } else {
      this.navigateIfNeeded(notification);
    }
  }

  private navigateIfNeeded(notification: Notification): void {
    this.closed.emit();

    if (notification.actionUrl) {
      void this.router.navigateByUrl(notification.actionUrl);
    }
  }

  onRetry(): void {
    this.summary.refetch();
  }

  onClose(): void {
    this.closed.emit();
  }

  trackById(index: number, item: Notification): string {
    return item.id;
  }

  private showErrorToast(key: string): void {
    this.messageService.add({
      severity: 'error',
      summary: this.translateService.instant('ALERT.ERROR'),
      detail: this.translateService.instant(key),
    });
  }
}
